import React, { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../../../config";

// The Actions column here was an empty placeholder, so the only way to approve
// a booking was the separate all-screens dashboard. It also called
// `req.proposed_rate.toFixed(2)` and `req.status.charAt(0)` unguarded, and both
// columns are nullable -- a single legacy row with no rate blanked the page.

const TERMINAL = ["cancelled", "rejected", "denied"];

function statusColour(status) {
    if (status === "approved") return "#4caf50";
    if (status === "rejected" || status === "denied" || status === "cancelled") return "#f44336";
    return "#ea476e";
}

function titleCase(value) {
    if (!value) return "Unknown";
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function money(value) {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function when(value) {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d) ? "—" : d.toLocaleDateString();
}

export default function DeviceRequestHistory({ deviceId }) {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");

    const token = localStorage.getItem("token");

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/device/${deviceId}/requests`, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            const data = await res.json();
            setRequests(Array.isArray(data) ? data : []);
        } catch {
            setRequests([]);
        }
        setLoading(false);
    }, [deviceId, token]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    // Pull one approved booking off the screen and refund it. Separate from
    // handleAction because it carries a reason -- this ends someone's paid
    // campaign mid-flight, and "why" is both the first thing they will ask and
    // the only defence if they dispute the charge.
    async function handleTakedown(requestId) {
        const reason = window.prompt(
            "Take this booking down?\n\n" +
            "It is cancelled and refunded, and the screen stops showing it at " +
            "its next check-in — within about a minute.\n\n" +
            "Reason (required):"
        );
        if (reason === null) return;
        if (!reason.trim()) {
            setError("A reason is required to take a booking down.");
            return;
        }
        setBusyId(requestId);
        setError("");
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/vendor/requests/${requestId}/takedown`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ reason }),
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || "Could not take this booking down.");
            } else if (data.refund) {
                // Says whether the money actually went back or is parked for a
                // human -- a booking that has already played is not a clean
                // refund, and pretending otherwise sets up a dispute.
                setError(data.refund);
            }
        } catch {
            setError("Network error. Nothing changed.");
        } finally {
            setBusyId(null);
            fetchRequests();
        }
    }

    async function handleAction(requestId, action) {
        setBusyId(requestId);
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/requests/${requestId}/${action}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                // Approve can fail with 409 when another booking already holds
                // one of these days, and clear with 409 when the booking still
                // owes the customer a refund. Both used to fail silently, so
                // the row simply did not change and nobody knew why.
                const data = await res.json().catch(() => ({}));
                setError(data.message || `Could not ${action} this booking.`);
            }
        } catch {
            setError("Network error. Nothing changed.");
        }
        setBusyId(null);
        fetchRequests();
    }

    return (
        <div style={{ marginTop: "2rem" }}>
            <h3>Requests for this screen</h3>
            {error && <div style={{ color: "#ea476e", marginBottom: 10 }}>{error}</div>}
            {loading ? (
                <div>Loading requests…</div>
            ) : requests.length === 0 ? (
                <div>No requests for this screen yet.</div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", background: "#fff", borderRadius: 6, borderCollapse: "collapse", marginTop: "1rem" }}>
                        <thead>
                            <tr style={{ background: "#ea476e22" }}>
                                <th style={{ padding: "0.5rem", textAlign: "left" }}>Customer</th>
                                <th style={{ padding: "0.5rem", textAlign: "left" }}>Dates</th>
                                <th style={{ padding: "0.5rem", textAlign: "right" }}>Per day</th>
                                <th style={{ padding: "0.5rem", textAlign: "right" }}>Total</th>
                                <th style={{ padding: "0.5rem", textAlign: "left" }}>Status</th>
                                <th style={{ padding: "0.5rem", textAlign: "left" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((req) => (
                                <tr key={req.id} style={{ borderBottom: "1px solid #eee" }}>
                                    <td style={{ padding: "0.5rem" }}>#{req.customer_id}</td>
                                    <td style={{ padding: "0.5rem" }}>
                                        {when(req.start_time)} – {when(req.end_time)}
                                    </td>
                                    <td style={{ padding: "0.5rem", textAlign: "right" }}>{money(req.proposed_rate)}</td>
                                    <td style={{ padding: "0.5rem", textAlign: "right" }}>{money(req.amount)}</td>
                                    <td style={{ padding: "0.5rem" }}>
                                        <span style={{ color: statusColour(req.status), fontWeight: 600 }}>
                                            {titleCase(req.status)}
                                        </span>
                                    </td>
                                    <td style={{ padding: "0.5rem" }}>
                                        {req.status === "pending" && (
                                            <>
                                                <button
                                                    disabled={busyId === req.id}
                                                    onClick={() => handleAction(req.id, "approve")}
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    disabled={busyId === req.id}
                                                    onClick={() => handleAction(req.id, "deny")}
                                                    style={{ marginLeft: 6 }}
                                                >
                                                    Deny
                                                </button>
                                            </>
                                        )}
                                        {req.status === "approved" && (
                                            <button
                                                disabled={busyId === req.id}
                                                onClick={() => handleTakedown(req.id)}
                                                title="Cancel and refund this booking, and stop the screen showing it"
                                            >
                                                Take down
                                            </button>
                                        )}
                                        {TERMINAL.includes(req.status) && (
                                            <button
                                                disabled={busyId === req.id}
                                                onClick={() => handleAction(req.id, "clear")}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
